<template>
  <div class="project-summary-spacer" aria-hidden="true"></div>
  <div class="project-summary-container" :style="projectSummaryContainerStyle">
    <div class="project-summary core-wrapper">
      <div class="title">{{ projectName }}</div>
      <ul class="value-list" style="display: none">
        <!-- TODO: check if we still need this, hidden for now -->
        <li v-if="hourlyRate" class="text-neutral-450">
          {{ getFormattedHourlyRate }}
        </li>
        <li class="text-neutral-450">{{ getFormattedArea }}</li>
      </ul>
      <div class="button-group">
        <template v-if="showProjectTransfer">
          <button
            v-tooltip="tooltip(t('general.transfer.export.title'))"
            type="button"
            class="core-transfer-trigger core-action-button core-action-button--supporting"
            data-testid="project-export-button"
            :aria-label="t('general.transfer.export.title')"
            @click.prevent="transferMode = 'export'">
            <IconFileExport class="h-5 w-5" stroke="1.8" />
          </button>
          <button
            v-tooltip="tooltip(t('general.transfer.import.title'))"
            type="button"
            class="core-transfer-trigger core-action-button core-action-button--supporting"
            data-testid="project-import-button"
            :aria-label="t('general.transfer.import.title')"
            @click.prevent="transferMode = 'import'">
            <IconFileImport class="h-5 w-5" stroke="1.8" />
          </button>
        </template>
        <LanguageSelector class="project-summary-language-selector" />
        <button
          v-if="showSubmitProjectData"
          @click.prevent="onSubmitProjectDataClick"
          class="core-button-supporting core-action-button core-action-button--supporting">
          {{ $t('general.show_submit_project_data') }}
        </button>
        <button
          v-if="feedbackUrl"
          @click.prevent="onFeedbackClick"
          class="core-button-supporting core-action-button core-action-button--supporting">
          {{ $t('general.button.feedback') }}
        </button>
        <button
          v-if="footerWikiButton"
          @click.prevent="onFooterWikiClick"
          class="core-button-supporting core-action-button core-action-button--supporting">
          {{ footerWikiButton.label }}
        </button>
        <button
          @click.prevent="onSaveProject"
          class="core-button-primary core-action-button core-action-button--primary">
          {{ $t('general.button.save') }}
        </button>
      </div>
    </div>
  </div>
  <ProjectTransferModal
    v-if="showProjectTransfer"
    :open="transferMode !== null"
    :mode="transferMode ?? 'export'"
    @close="transferMode = null" />
</template>

<script lang="ts" setup>
import { formatNumber } from '@/helpers/format'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { IconFileExport, IconFileImport } from '@tabler/icons-vue'
import { useClientStore } from '@/stores/client'
import { useProjectStore } from '@/stores/project'
import LanguageSelector from '@/components/partials/navigation/LanguageSelector.vue'
import ProjectTransferModal, { type ProjectTransferMode } from '@/components/partials/modals/ProjectTransferModal.vue'
import { useFooterLayoutState } from '@/helpers/footer-layout-state'
import { useRoute, useRouter } from 'vue-router'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const clientStore = useClientStore()
const projectStore = useProjectStore()
const transferMode = ref<ProjectTransferMode | null>(null)
const showProjectTransfer = computed(() => !!projectStore.getProject)
const tooltip = (text: string) => ({ text, theme: { placement: 'top' } })
const {
  isNormalFooterVisible,
  normalFooterHeight,
  scrollbarWidth,
  registerProjectSummaryContainer,
  unregisterProjectSummaryContainer,
} = useFooterLayoutState()
const feedbackUrl = computed(() => clientStore.feedback_url)
const showSubmitProjectData = computed(() => clientStore.show_submit_project_data)
const DEFAULT_WIKI_LABEL = 'Wiki'
const projectSummaryContainerStyle = computed(() => ({
  right: `${scrollbarWidth.value}px`,
  transform: isNormalFooterVisible.value ? `translateY(-${normalFooterHeight.value}px)` : 'translateY(0px)',
}))

function isValidHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

const footerWikiButton = computed(() => {
  if (!clientStore.footer_wiki_button_enabled) {
    return null
  }

  const label = (clientStore.footer_wiki_button_label || DEFAULT_WIKI_LABEL).trim()
  const url = (clientStore.footer_wiki_button_url || '').trim()

  if (!label || !isValidHttpUrl(url)) {
    return null
  }

  return { label, url }
})

const unit = {
  rate: 'CHF/h',
  hour: 'h',
  area: 'm²',
}
const emit = defineEmits(['onSaveProject', 'onCloseProject'])

const props = defineProps<{
  projectName?: string
  hourlyRate?: number
  area?: number
  // complexity: number | null = null;
  // hours: number | null = null;
}>()

const getFormattedHourlyRate = computed((): string => {
  return props.hourlyRate ? `${formatNumber(props.hourlyRate, "'")} ${unit.rate}` : `0 ${unit.rate}`
})
const getFormattedArea = computed((): string => {
  return props.area ? `${formatNumber(props.area)} ${unit.area}` : ''
})
// get formattedHours(): string {
//   return this.hours ? `${formatNumber(this.hours, "'")} ${unit.hour}` : '';
// }
//
// get formattedComplexity(): string {
//   return this.complexity ? `${this.complexity}` : '';
// }

function onSubmitProjectDataClick() {
  if (route.name === 'summary-project-view') {
    document.getElementById('project-submission')?.scrollIntoView({ behavior: 'smooth' })
  } else {
    router.push({ name: 'summary-project-view', hash: '#project-submission' })
  }
}

function onSaveProject() {
  emit('onSaveProject')
}

function onFeedbackClick() {
  if (feedbackUrl.value) {
    window.open(feedbackUrl.value, '_blank')
  }
}

function onFooterWikiClick() {
  if (footerWikiButton.value) {
    window.open(footerWikiButton.value.url, '_blank')
  }
}

onMounted(() => {
  registerProjectSummaryContainer()
})

onUnmounted(() => {
  unregisterProjectSummaryContainer()
})
</script>

<style lang="postcss" scoped>
.project-summary-container {
  @apply fixed bottom-0 left-0 right-0 z-40;
  @apply flex min-h-20 items-center py-3;
  @apply bg-white border-t border-neutral-300;
  transition: transform 0.2s ease;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.05),
    0 -8px 16px -10px rgba(0, 0, 0, 0.09);

  .project-summary {
    @apply flex flex-row items-center justify-between gap-3;
    .title {
      @apply hidden lg:block;
      max-width: 25%;
    }
    .value-list {
      @apply hidden w-1/2 flex-col items-center lg:flex lg:flex-row lg:justify-evenly;
      @apply m-0 list-none px-8;
    }
    .button-group {
      @apply ml-auto min-w-0 flex-1 flex flex-row flex-wrap items-center justify-end gap-3 md:gap-4;
    }
    .project-summary-language-selector {
      @apply w-full md:w-auto;
    }
    .project-summary-language-selector :deep(.language-trigger) {
      @apply w-full justify-center md:w-auto md:justify-start;
    }
    button {
      @apply w-full px-6 py-3 md:w-auto;
      @apply font-semibold;
    }
    .core-transfer-trigger {
      @apply h-11 w-11 shrink-0 !px-0 !py-0 md:w-11;
    }
  }
}

.project-summary-spacer {
  height: 161px;
}
</style>
